"use client";

import { useState, useMemo } from "react";
import Select from "react-select";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { multiTokenSearch } from "./searchUtils";

const multiSelectFilterStyles = {
  control: (provided) => ({
    ...provided,
    backgroundColor: "#1f2937",
    borderColor: "#4b5563",
    minHeight: "50px",
    fontSize: "14px",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 999999 }),
  menu: (base) => ({
    ...base,
    backgroundColor: "#374151",
    borderRadius: "0.5rem",
    boxShadow: "0 10px 40px rgba(0, 0, 0, 0.5)",
    border: "1px solid #4b5563",
    width: "320px",
    maxWidth: "320px",
  }),
  menuList: (base) => ({ ...base, maxHeight: "380px", padding: "8px" }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? "#4b5563" : "#374151",
    color: state.isSelected ? "#f97316" : "#e5e7eb",
    padding: "10px 12px",
    fontSize: "13.5px",
    whiteSpace: "normal",
    wordBreak: "break-word",
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "4px 8px",
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "#4b5563",
    borderRadius: "6px",
    fontSize: "13px",
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "#e5e7eb",
    padding: "3px 8px",
    paddingRight: "6px",
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: "#9ca3af",
    ":hover": { backgroundColor: "#f97316", color: "white" },
  }),
};

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#4b5563",
    borderColor: state.isFocused ? "#f97316" : "#6b7280",
    color: "#e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 1px #f97316" : "none",
    "&:hover": { borderColor: "#f97316" },
    minHeight: "38px",
    fontSize: "12px",
  }),
  input: (provided) => ({ ...provided, color: "#e5e7eb" }),
  singleValue: (provided) => ({
    ...provided,
    fontSize: "12px",
    color: "#e5e7eb",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#4b5563" : "#374151",
    color: state.isSelected ? "#f97316" : "#e5e7eb",
    "&:hover": { backgroundColor: "#6b7280" },
    fontSize: "12px",
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#374151",
    border: "1px solid #4b5563",
    zIndex: 9999,
  }),
  menuPortal: (provided) => ({ ...provided, zIndex: 999999 }),
  placeholder: (provided) => ({ ...provided, color: "#9ca3af" }),
};

export default function DatabaseItemsModal({
  open,
  onClose,
  databaseItems = [],
  loading = false,
  databaseFilters = { category: [], uom: [], search: "" },
  setDatabaseFilters,
  selectedDatabaseItems = [],
  setSelectedDatabaseItems,
  onAddItems,
  formatCurrency,
  formData,
  parseSizeToCoverage,
  isSFUnit,
  isLFUnit,
  getDatabaseFilterOptions,
}) {
  const [showFilter, setShowFilter] = useState(null);

  const filteredItems = useMemo(() => {
    if (!Array.isArray(databaseItems)) return [];

    let result = databaseItems;

    if (databaseFilters.category.length > 0) {
      result = result.filter((item) =>
        databaseFilters.category.includes(item?.category),
      );
    }

    if (databaseFilters.uom.length > 0) {
      result = result.filter((item) => databaseFilters.uom.includes(item?.uom));
    }

    if (databaseFilters.search?.trim()) {
      result = multiTokenSearch(result, databaseFilters.search);
    }

    return result;
  }, [databaseItems, databaseFilters]);

  const totalEstimate = useMemo(() => {
    const markup = formData?.defaultMarkupPercentage || 30;
    return selectedDatabaseItems.reduce((sum, sel) => {
      const item = databaseItems.find((i) => i.itemCode === sel.itemCode);
      if (!item) return sum;

      let unitCost = item.defaultCost || 0;
      if (sel.selectedVendor && Array.isArray(item.vendorPrices)) {
        const vp = item.vendorPrices.find(
          (p) => (p.vendor?._id || p.vendor) === sel.selectedVendor,
        );
        if (vp) unitCost = vp.price || 0;
      } else {
        // Check default vendor price even when selectedVendor matches defaultVendor
        const defaultVendorId =
          item.defaultVendor?._id || item.defaultVendor || null;
        if (defaultVendorId && Array.isArray(item.vendorPrices)) {
          const vp = item.vendorPrices.find(
            (p) => (p.vendor?._id || p.vendor) === defaultVendorId,
          );
          if (vp) unitCost = vp.price || unitCost;
        }
      }

      const unitPrice = unitCost * (1 + markup / 100);
      const qty = Math.abs(sel.deltaQty || 1);
      const coverage = sel.editedCoverage || 1;
      const isCoverageItem =
        item.category === "M" && (isSFUnit(item.uom) || isLFUnit(item.uom));

      return (
        sum + (isCoverageItem ? qty * coverage * unitPrice : qty * unitPrice)
      );
    }, 0);
  }, [selectedDatabaseItems, databaseItems, formData, isSFUnit, isLFUnit]);

  // REPLACE the existing handleToggle function
  const handleToggle = (item) => {
    setSelectedDatabaseItems((prev) => {
      const exists = prev.find((i) => i.itemCode === item.itemCode);
      if (exists) return prev.filter((i) => i.itemCode !== item.itemCode);

      const isCoverageItem =
        item.category === "M" && (isSFUnit(item.uom) || isLFUnit(item.uom));

      let editedSize = normalizeSize(item.size || "", item.uom);

      // ── KEY FIX: Derive coverage from the normalized size string ──
      let coverage = parseFloat(item.coveragePerUnit) || 1;
      if (isCoverageItem && editedSize) {
        const parsed = parseSizeToCoverage(editedSize, item.uom);
        if (parsed !== null) coverage = parsed;
      }

      if (!editedSize && isCoverageItem && coverage === 1) {
        editedSize = isSFUnit(item.uom) ? "1' x 1'" : "1'";
      }

      // Auto-select default vendor and use its price
      let selectedVendor = null;
      let effectiveCost = item.defaultCost || 0;

      const defaultVendorId =
        item.defaultVendor?._id || item.defaultVendor || null;

      if (defaultVendorId && Array.isArray(item.vendorPrices)) {
        const vendorPrice = item.vendorPrices.find(
          (vp) => (vp.vendor?._id || vp.vendor) === defaultVendorId,
        );
        if (vendorPrice) {
          selectedVendor = defaultVendorId;
          effectiveCost = vendorPrice.price || effectiveCost;
        }
      }

      return [
        ...prev,
        {
          ...item,
          deltaQty: 1,
          editedSize,
          editedCoverage: coverage,
          selectedVendor,
          effectiveCost,
        },
      ];
    });
  };

  const handleItemChange = (itemCode, field, value) => {
    setSelectedDatabaseItems((prev) =>
      prev.map((i) => {
        if (i.itemCode !== itemCode) return i;
        const updated = { ...i, [field]: value };
        if (field === "editedSize") {
          const newCoverage = parseSizeToCoverage(value, i.uom);
          if (newCoverage !== null) updated.editedCoverage = newCoverage;
        }
        return updated;
      }),
    );
  };

  // Helper to normalize size strings like "1x1" to "1' x 1'"
  const normalizeSize = (sizeStr, uom) => {
    if (!sizeStr) return "";
    const str = sizeStr.trim();

    // Already has foot/inch markers — return as-is (e.g., "4'x8'", "4' x 8'", "10'")
    if (str.includes("'") || str.includes('"')) {
      return str;
    }

    // Simple "NxN" or "N x N" pattern without foot markers → add them for SF
    const dimPattern = /^(\d+(?:\.\d+)?)\s*[xX×]\s*(\d+(?:\.\d+)?)$/;
    const dimMatch = str.match(dimPattern);

    if (dimMatch && isSFUnit(uom)) {
      return `${dimMatch[1]}' x ${dimMatch[2]}'`;
    }

    // Single number without foot marker → add it for LF
    const singlePattern = /^(\d+(?:\.\d+)?)$/;
    const singleMatch = str.match(singlePattern);

    if (singleMatch && isLFUnit(uom)) {
      return `${singleMatch[1]}'`;
    }

    // Single number for SF items (e.g., area value) — return as-is
    return str;
  };

  const toggleFilter = (field) => {
    setShowFilter((prev) => (prev === field ? null : field));
  };

  const clearAllFilters = () => {
    setDatabaseFilters({ category: [], uom: [], search: "" });
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div
        className="bg-gray-800 rounded-lg shadow-2xl flex flex-col"
        style={{ width: "1400px", height: "85vh" }}
      >
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b border-gray-600 flex-shrink-0">
          <h2 className="text-xl font-semibold text-white">
            Add Items from Database
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Filters Bar */}
        <div className="p-4 border-b border-gray-600 bg-gray-800/95 flex-shrink-0">
          <div className="flex flex-wrap items-start gap-4">
            <div className="relative">
              <input
                type="text"
                value={databaseFilters.search}
                onChange={(e) =>
                  setDatabaseFilters((prev) => ({
                    ...prev,
                    search: e.target.value,
                  }))
                }
                placeholder="Search code, description..."
                className="w-64 px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded text-gray-200 placeholder-gray-500"
              />
              {databaseFilters.search && (
                <button
                  onClick={() =>
                    setDatabaseFilters((prev) => ({ ...prev, search: "" }))
                  }
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  ×
                </button>
              )}
            </div>

            <div className="flex flex-wrap items-start gap-3">
              {/* Category Filter */}
              <div className="relative">
                <button
                  onClick={() => toggleFilter("category")}
                  className={`px-3 py-1.5 text-xs rounded flex items-center gap-1 ${
                    databaseFilters.category.length > 0
                      ? "bg-blue-600/80 text-white"
                      : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                  }`}
                >
                  Category
                  {databaseFilters.category.length > 0 && (
                    <span className="ml-1 px-1 py-0.5 text-[10px] bg-black/30 rounded-full">
                      {databaseFilters.category.length}
                    </span>
                  )}
                </button>

                {showFilter === "category" && (
                  <div className="absolute top-full mt-2 left-0 z-50 w-80">
                    <div className="bg-gray-700 rounded-lg shadow-2xl border border-gray-600 p-2">
                      <Select
                        isMulti
                        autoFocus
                        options={[
                          { value: "M", label: "Material" },
                          { value: "L", label: "Labor" },
                        ]}
                        value={[
                          { value: "M", label: "Material" },
                          { value: "L", label: "Labor" },
                        ].filter((o) =>
                          databaseFilters.category.includes(o.value),
                        )}
                        onChange={(opts) =>
                          setDatabaseFilters((prev) => ({
                            ...prev,
                            category: opts ? opts.map((o) => o.value) : [],
                            showFilter: null,
                          }))
                        }
                        menuIsOpen
                        menuPortalTarget={document.body}
                        styles={multiSelectFilterStyles}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* UOM Filter */}
              <div className="relative">
                <button
                  onClick={() => toggleFilter("uom")}
                  className={`px-3 py-1.5 text-xs rounded flex items-center gap-1 ${
                    databaseFilters.uom.length > 0
                      ? "bg-purple-600/80 text-white"
                      : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                  }`}
                >
                  UOM
                  {databaseFilters.uom.length > 0 && (
                    <span className="ml-1 px-1 py-0.5 text-[10px] bg-black/30 rounded-full">
                      {databaseFilters.uom.length}
                    </span>
                  )}
                </button>

                {showFilter === "uom" && (
                  <div className="absolute top-full mt-2 left-0 z-50 w-80">
                    <div className="bg-gray-700 rounded-lg shadow-2xl border border-gray-600 p-2">
                      <Select
                        isMulti
                        autoFocus
                        options={getDatabaseFilterOptions("uom")}
                        value={getDatabaseFilterOptions("uom").filter((o) =>
                          databaseFilters.uom.includes(o.value),
                        )}
                        onChange={(opts) =>
                          setDatabaseFilters((prev) => ({
                            ...prev,
                            uom: opts ? opts.map((o) => o.value) : [],
                            showFilter: null,
                          }))
                        }
                        menuIsOpen
                        menuPortalTarget={document.body}
                        styles={multiSelectFilterStyles}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Clear All */}
              {(databaseFilters.search ||
                databaseFilters.category.length > 0 ||
                databaseFilters.uom.length > 0) && (
                <button
                  onClick={clearAllFilters}
                  className="px-3 py-1.5 text-xs text-red-400 hover:text-red-300 rounded hover:bg-red-900/30"
                >
                  Clear all
                </button>
              )}
            </div>

            <span className="text-sm text-gray-400 ml-auto">
              <span className="text-white font-semibold">
                {filteredItems.length}
              </span>
              {filteredItems.length !== databaseItems.length && (
                <span className="text-gray-500"> / {databaseItems.length}</span>
              )}{" "}
              items
            </span>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto p-4">
          {loading ? (
            <div className="text-center text-gray-400 py-8">
              Loading items...
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center text-gray-400 py-8">
              No items found.
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-600">
              <thead className="bg-gray-700 sticky top-0 z-10">
                <tr>
                  <th className="px-2 py-2 text-left text-xs text-gray-400">
                    Select
                  </th>
                  <th className="px-2 py-2 text-left text-xs text-gray-400">
                    Cat
                  </th>
                  <th className="px-2 py-2 text-left text-xs text-gray-400">
                    Item Code
                  </th>
                  <th className="px-3 py-2 text-left text-xs text-gray-400">
                    Description
                  </th>
                  <th className="px-2 py-2 text-left text-xs text-gray-400">
                    Size
                  </th>
                  <th className="px-2 py-2 text-left text-xs text-gray-400">
                    UOM
                  </th>
                  <th className="px-2 py-2 text-left text-xs text-gray-400 text-right">
                    Coverage
                  </th>
                  <th className="px-2 py-2 text-left text-xs text-gray-400 text-right">
                    Cost / Unit
                  </th>

                  <th className="px-2 py-2 text-left text-xs text-gray-400">
                    Vendor
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-600">
                {filteredItems.map((item) => {
                  const isSelected = selectedDatabaseItems.some(
                    (s) => s.itemCode === item.itemCode,
                  );
                  const sel = selectedDatabaseItems.find(
                    (s) => s.itemCode === item.itemCode,
                  );
                  const isCoverageItem =
                    item.category === "M" &&
                    (isSFUnit(item.uom) || isLFUnit(item.uom));

                  let unitCost = item.defaultCost || 0;
                  if (sel?.selectedVendor && Array.isArray(item.vendorPrices)) {
                    const vp = item.vendorPrices.find(
                      (p) => (p.vendor?._id || p.vendor) === sel.selectedVendor,
                    );
                    if (vp) unitCost = vp.price || 0;
                  } else if (!sel?.selectedVendor) {
                    // If no vendor selected, still check if default vendor has a price
                    const defaultVendorId =
                      item.defaultVendor?._id || item.defaultVendor || null;
                    if (defaultVendorId && Array.isArray(item.vendorPrices)) {
                      const vp = item.vendorPrices.find(
                        (p) => (p.vendor?._id || p.vendor) === defaultVendorId,
                      );
                      if (vp) unitCost = vp.price || unitCost;
                    }
                  }
                  const markup = formData?.defaultMarkupPercentage || 30;
                  const unitPrice = unitCost * (1 + markup / 100);
                  const coverage = sel?.editedCoverage || 1;
                  const qty = Math.abs(sel?.deltaQty || 1);
                  const estTotal = isCoverageItem
                    ? qty * coverage * unitPrice
                    : qty * unitPrice;

                  const vendorOptions = Array.isArray(item?.vendorPrices)
                    ? item.vendorPrices.map((vp) => ({
                        value: vp.vendor?._id,
                        label: `${
                          vp.vendor?.companyName || "Unknown"
                        } - ${formatCurrency(vp.price || 0)}`,
                      }))
                    : [];

                  return (
                    <tr
                      key={item.itemCode}
                      className={
                        isSelected ? "bg-blue-900/20" : "hover:bg-gray-700"
                      }
                    >
                      <td className="px-2 py-2">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggle(item)}
                          className="h-4 w-4 text-blue-500 rounded"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <span
                          className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${
                            item.category === "M"
                              ? "bg-blue-900/50 text-blue-300"
                              : "bg-orange-900/50 text-orange-300"
                          }`}
                        >
                          {item.category}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-sm text-gray-200 font-mono">
                        {item.itemCode}
                      </td>
                      <td
                        className="px-3 py-2 text-sm text-gray-200 max-w-xs truncate"
                        title={item.description}
                      >
                        {item.description}
                      </td>
                      <td className="px-2 py-2">
                        {isSelected && item.category === "M" ? (
                          <input
                            type="text"
                            value={sel?.editedSize || ""}
                            onChange={(e) =>
                              handleItemChange(
                                item.itemCode,
                                "editedSize",
                                e.target.value,
                              )
                            }
                            placeholder={
                              isCoverageItem
                                ? isSFUnit(item.uom)
                                  ? "e.g. 4'x8' or 1' x 1'"
                                  : "e.g. 10' or 1'"
                                : "Size (optional)"
                            }
                            className="w-32 rounded bg-gray-600 border-gray-500 text-gray-200 text-sm py-1 px-2 font-mono"
                          />
                        ) : (
                          <span className="text-sm text-gray-300 font-mono">
                            {item.size ||
                              (isCoverageItem &&
                              // If no size but coverage is exactly 1 → show 1' x 1' or 1'
                              (sel?.editedCoverage ||
                                parseFloat(item.coveragePerUnit) ||
                                0) === 1
                                ? isSFUnit(item.uom)
                                  ? "1' x 1'"
                                  : "1'"
                                : "-")}
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-sm text-gray-300">
                        {item.uom || "-"}
                        {isCoverageItem && (
                          <span className="block text-[10px] text-teal-400">
                            per {isSFUnit(item.uom) ? "SF" : "LF"}
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-right">
                        {isSelected && isCoverageItem ? (
                          <div className="flex flex-col items-end">
                            <input
                              type="number"
                              step="0.01"
                              value={sel?.editedCoverage || ""}
                              onChange={(e) =>
                                handleItemChange(
                                  item.itemCode,
                                  "editedCoverage",
                                  parseFloat(e.target.value) || 1,
                                )
                              }
                              className="w-20 rounded bg-gray-600 border-gray-500 text-gray-200 text-sm py-1 px-2 text-right"
                            />
                            <span className="text-[10px] text-teal-400">
                              {isSFUnit(item.uom) ? "SF" : "LF"}/unit
                            </span>
                          </div>
                        ) : isCoverageItem ? (
                          <span className="text-sm text-gray-400">
                            {coverage.toFixed(2)}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-500">-</span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-sm text-gray-300 text-right font-mono">
                        {formatCurrency(unitCost)}
                        {isCoverageItem && (
                          <span className="block text-[10px] text-gray-500">
                            /{isSFUnit(item.uom) ? "SF" : "LF"}
                          </span>
                        )}
                      </td>

                      <td className="px-2 py-2">
                        {isSelected && vendorOptions.length > 0 ? (
                          <Select
                            options={vendorOptions}
                            value={
                              vendorOptions.find(
                                (v) => v.value === sel?.selectedVendor,
                              ) ||
                              vendorOptions.find(
                                (v) =>
                                  v.value ===
                                  (item.defaultVendor?._id ||
                                    item.defaultVendor),
                              ) ||
                              null
                            }
                            onChange={(opt) =>
                              handleItemChange(
                                item.itemCode,
                                "selectedVendor",
                                opt?.value || null,
                              )
                            }
                            styles={customStyles}
                            placeholder="Default"
                            isClearable
                            menuPortalTarget={document.body}
                          />
                        ) : vendorOptions.length > 0 ? (
                          <span className="text-xs text-gray-400">
                            {(() => {
                              const defaultVendorId =
                                item.defaultVendor?._id || item.defaultVendor;
                              const defaultVendorOpt = vendorOptions.find(
                                (v) => v.value === defaultVendorId,
                              );
                              return defaultVendorOpt
                                ? defaultVendorOpt.label.split(" - ")[0]
                                : `${vendorOptions.length} vendor(s)`;
                            })()}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-500">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-600 flex justify-between items-center bg-gray-800">
          <div className="flex items-center gap-6">
            <span className="text-sm text-gray-400">
              <span className="text-blue-400 font-semibold">
                {selectedDatabaseItems.length}
              </span>{" "}
              item(s) selected
            </span>
            {selectedDatabaseItems.length > 0 && (
              <span className="text-lg text-gray-300">
                Total Estimate:{" "}
                <span className="text-green-400 font-bold">
                  {formatCurrency(totalEstimate)}
                </span>
              </span>
            )}
          </div>
          <div className="space-x-3">
            <button
              onClick={onClose}
              className="px-5 py-2 text-sm bg-gray-600 rounded hover:bg-gray-500 text-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={onAddItems}
              disabled={selectedDatabaseItems.length === 0}
              className={`px-6 py-2 text-sm text-white rounded font-medium ${
                selectedDatabaseItems.length > 0
                  ? "bg-blue-600 hover:bg-blue-700"
                  : "bg-gray-600 cursor-not-allowed opacity-70"
              }`}
            >
              Add Selected Items
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
